import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Instagram, Smartphone } from "lucide-react";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", date: "", time: "", guests: 2, occasion: "", message: "" };

const FIELD = "w-full bg-transparent border-b border-[#C8A24A]/40 focus:border-[#C8A24A] outline-none py-3 text-white placeholder:text-[#D6D6D6]/40 transition-colors focus:shadow-[0_2px_12px_-6px_rgba(200,162,74,0.6)]";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, { ...form, guests: Number(form.guests) });
      toast.success("Reservation request received! We'll call you shortly to confirm.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call us at (548) 889-8500.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#0D0D0D] py-24 md:py-32 px-6 md:px-10 border-t border-[#C8A24A]/10">
      <div className="mx-auto max-w-[1400px]">
        <Reveal className="text-center mb-16">
          <p className="text-[#C8A24A] uppercase text-xs tracking-[0.35em] mb-4">Reserve & Visit</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl">
            Book Your <span className="italic text-gold-gradient">Table</span>
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          {/* Form */}
          <Reveal>
            <form onSubmit={submit} data-testid="reservation-form" className="space-y-7">
              <div className="grid sm:grid-cols-2 gap-7">
                <input required name="name" value={form.name} onChange={change} placeholder="Full Name" data-testid="form-name" className={FIELD} />
                <input required type="email" name="email" value={form.email} onChange={change} placeholder="Email Address" data-testid="form-email" className={FIELD} />
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <input required name="phone" value={form.phone} onChange={change} placeholder="Phone Number" data-testid="form-phone" className={FIELD} />
                <select name="guests" value={form.guests} onChange={change} data-testid="form-guests" className={`${FIELD} appearance-none [&>option]:bg-[#181818]`}>
                  {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n} {n === 1 ? "Guest" : "Guests"}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#C8A24A]/70">Date</label>
                  <input required type="date" name="date" value={form.date} onChange={change} data-testid="form-date" className={`${FIELD} [color-scheme:dark]`} />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#C8A24A]/70">Time</label>
                  <input required type="time" name="time" value={form.time} onChange={change} data-testid="form-time" className={`${FIELD} [color-scheme:dark]`} />
                </div>
              </div>
              <input name="occasion" value={form.occasion} onChange={change} placeholder="Occasion (optional)" data-testid="form-occasion" className={FIELD} />
              <textarea name="message" value={form.message} onChange={change} placeholder="Special requests (optional)" rows={3} data-testid="form-message" className={`${FIELD} resize-none`} />
              <motion.button
                whileTap={{ scale: 0.97 }}
                disabled={loading}
                type="submit"
                data-testid="reservation-submit"
                className="btn-gold w-full sm:w-auto bg-gradient-to-r from-[#C8A24A] to-[#E0C067] text-[#0D0D0D] px-12 py-4 rounded-sm text-sm tracking-[0.16em] uppercase font-semibold disabled:opacity-60"
              >
                {loading ? "Sending…" : "Request Reservation"}
              </motion.button>
            </form>
          </Reveal>

          {/* Info + Map */}
          <Reveal delay={0.15} className="space-y-8">
            <div className="grid sm:grid-cols-2 gap-6">
              <InfoCard icon={MapPin} title="Address" lines={["700 Strasburg Road", "Kitchener, Ontario"]} />
              <InfoCard icon={Clock} title="Hours" lines={["Every Day", "2:00 PM – 2:00 AM"]} />
              <InfoCard icon={Phone} title="Phone" lines={["(548) 889-8500"]} href="tel:+15488898500" />
              <InfoCard icon={Smartphone} title="Mobile" lines={["(548) 886-8500"]} href="tel:+15488868500" />
            </div>

            <a href="https://www.instagram.com/ekaa_cuisine" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="hover-gold-glow flex items-center gap-4 bg-[#181818] border border-[#C8A24A]/15 rounded-sm p-5">
              <Instagram className="h-6 w-6 text-[#C8A24A]" />
              <div>
                <p className="text-white font-medium">@ekaa_cuisine</p>
                <p className="text-[#D6D6D6]/60 text-xs">Follow us on Instagram</p>
              </div>
            </a>

            <div className="overflow-hidden rounded-sm border border-[#C8A24A]/20 h-64">
              <iframe
                title="Ekaa location map"
                data-testid="google-map"
                src="https://maps.google.com/maps?q=700%20Strasburg%20Road%2C%20Kitchener%2C%20Ontario&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full grayscale contrast-125"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, title, lines, href }) {
  const inner = (
    <div className="hover-gold-glow h-full bg-[#181818] border border-[#C8A24A]/15 rounded-sm p-5">
      <Icon className="h-5 w-5 text-[#C8A24A] mb-3" />
      <p className="text-[10px] tracking-[0.25em] uppercase text-[#C8A24A]/70 mb-1">{title}</p>
      {lines.map((l) => <p key={l} className="text-[#D6D6D6] text-sm">{l}</p>)}
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : inner;
}
